import { OrderHistory, QuantityInfo } from '../../stores/appOrderHistoryStore';
import { RevenueHistory } from '../../services/revenuehistory/revenueHistoryService';
import { PriceAlert } from '../../services/priceAlerts.ts/priceAlertsService';
import { ReportSettings } from '../../services/restaurant/restaurantService';
import { RevenueSourceType } from '../../types/RevenueSourceType';
import { getItemTotalRevenue } from '../revenueAddOns';
import { calculateProductTotal, getProductQuantity } from '../platformUtils';
import { getOrderTotal } from '../../components/orderhistory/vatUtils';
import { filterRevenueByOverallType } from '../../components/revenuehistory/utils';
import { filterOrdersByOverallType } from '../../components/orderhistory/utils';
import { aggregateQuantityInfos, formatAggregatedQuantity } from '../quantityUtils';
import { ReportRange, inRange } from './shared';

/**
 * Pure, React-free computation for the Period Overview report (R1). This is a
 * frontend port of the weekly report email math (`server/domains/weeklyReports/`),
 * so the week view matches the email 1:1 for the same restaurant + week. All € on
 * the ex-VAT basis except the highest-spending section (parity exception — see
 * `computeHighestSpending`). Inputs are already scoped to the selected restaurant
 * by the data-loading hooks; period filtering happens here against `ReportRange`.
 */

/** Current-vs-previous comparison for one figure. `change` is the absolute delta
 * (€ for money, percentage points for inslag); `percentChange` mirrors the server
 * `calculateChange`. */
export interface SnapshotMetric {
  current: number;
  previous: number;
  change: number;
  percentChange: number;
}

/** One side (food/drinks/total) of the snapshot cards. */
export interface SnapshotSide {
  revenue: SnapshotMetric;
  costs: SnapshotMetric;
  inslag: SnapshotMetric;
}

export interface OverviewSnapshot {
  food: SnapshotSide;
  drinks: SnapshotSide;
  total: SnapshotSide;
  nonFoodCosts: SnapshotMetric;
}

export interface SellingItemRow {
  name: string;
  quantity: number;
  quantityChangePercent: number;
  revenue: number;
}

export interface TrackedDishRow {
  name: string;
  category: 'food' | 'drinks';
  quantity: number;
  quantityChange: number;
  revenue: number;
  revenueChange: number;
}

export interface SpendingProductRow {
  name: string;
  /** Supplier platform. */
  supplier: string;
  /** Σ getProductQuantity (fallback when no quantityInfo; matches the weekly email). */
  quantity: number;
  /** Spend INCL. tax (parity exception). */
  spent: number;
  /** Display-friendly aggregated quantity (kg/L/btl/pcs), undefined when no quantityInfo. */
  formattedQuantity?: string;
}

export interface PriceAlertRow {
  productName: string;
  platform?: string;
  fromPrice: number;
  toPrice: number;
  changePercent: number;
}

export interface OverviewReportData {
  snapshot: OverviewSnapshot;
  topSellingFood: SellingItemRow[];
  topSellingDrinks: SellingItemRow[];
  trackedDishes: TrackedDishRow[];
  highestSpendingKitchen: SpendingProductRow[];
  highestSpendingBar: SpendingProductRow[];
  priceAlerts: PriceAlertRow[];
  /** True for the "all" view, where no equal-length previous period exists. */
  comparisonHidden: boolean;
}

const TOP_LIMIT = 5;

// --- shared helpers -------------------------------------------------------

/** Ex-VAT revenue total for one revenue entry (item + nested choices). */
function revenueEntryTotal(rev: RevenueHistory): number {
  let total = 0;
  for (const category of rev.revenueData.categories) {
    for (const item of category.items) {
      total += getItemTotalRevenue(item, true);
    }
  }
  return total;
}

/** Ex-VAT revenue for one side, mirroring `snapshotOverviewService` (food/drinks
 * via `revenueSourceType`). */
function sideRevenue(revenue: RevenueHistory[], side: 'food' | 'drinks'): number {
  return filterRevenueByOverallType(revenue, side).reduce((sum, rev) => sum + revenueEntryTotal(rev), 0);
}

/** Ex-VAT product spend (price × qty) for one side, mirroring `calculateCosts` on
 * orders filtered by section type with NON_FOOD excluded (food/drinks) or kept
 * (non-food). */
function sideCosts(orders: OrderHistory[], side: 'food' | 'drinks' | 'non-food'): number {
  return filterOrdersByOverallType(orders, side).reduce(
    (sum, order) =>
      sum + order.productData.reduce((ps, product) => ps + calculateProductTotal(product, order.platform, true), 0),
    0
  );
}

/** Total ex-VAT order spend = Σ stored `order.total` (includes shipping/deposits),
 * mirroring the snapshot's total-costs card (intentionally differs from Σ price×qty). */
function totalOrderCosts(orders: OrderHistory[]): number {
  return orders.reduce((sum, order) => sum + getOrderTotal(order, true), 0);
}

/** Mirrors the server `calculateChange`: € delta + relative %. */
function metric(current: number, previous: number): SnapshotMetric {
  const change = current - previous;
  const percentChange = previous !== 0 ? (change / previous) * 100 : current > 0 ? 100 : 0;
  return { current, previous, change, percentChange };
}

function inslag(costs: number, revenue: number): number {
  return revenue > 0 ? (costs / revenue) * 100 : 0;
}

// --- snapshot -------------------------------------------------------------

function computeSnapshot(
  curOrders: OrderHistory[],
  curRevenue: RevenueHistory[],
  prevOrders: OrderHistory[],
  prevRevenue: RevenueHistory[]
): OverviewSnapshot {
  const build = (orders: OrderHistory[], revenue: RevenueHistory[]) => {
    const foodRevenue = sideRevenue(revenue, 'food');
    const drinksRevenue = sideRevenue(revenue, 'drinks');
    const foodCosts = sideCosts(orders, 'food');
    const drinksCosts = sideCosts(orders, 'drinks');
    const nonFoodCosts = sideCosts(orders, 'non-food');
    return {
      foodRevenue,
      drinksRevenue,
      // Total revenue = food + drinks only (mirrors snapshotOverviewService:145).
      totalRevenue: foodRevenue + drinksRevenue,
      foodCosts,
      drinksCosts,
      nonFoodCosts,
      // Total costs card = Σ order.total (snapshotOverviewService:167).
      totalCosts: totalOrderCosts(orders),
      foodInslag: inslag(foodCosts, foodRevenue),
      drinksInslag: inslag(drinksCosts, drinksRevenue),
      // Total inslag denominator = food + drinks revenue only (:175-179).
      totalInslag: inslag(foodCosts + drinksCosts, foodRevenue + drinksRevenue),
    };
  };

  const cur = build(curOrders, curRevenue);
  const prev = build(prevOrders, prevRevenue);

  return {
    food: {
      revenue: metric(cur.foodRevenue, prev.foodRevenue),
      costs: metric(cur.foodCosts, prev.foodCosts),
      inslag: metric(cur.foodInslag, prev.foodInslag),
    },
    drinks: {
      revenue: metric(cur.drinksRevenue, prev.drinksRevenue),
      costs: metric(cur.drinksCosts, prev.drinksCosts),
      inslag: metric(cur.drinksInslag, prev.drinksInslag),
    },
    total: {
      revenue: metric(cur.totalRevenue, prev.totalRevenue),
      costs: metric(cur.totalCosts, prev.totalCosts),
      inslag: metric(cur.totalInslag, prev.totalInslag),
    },
    nonFoodCosts: metric(cur.nonFoodCosts, prev.nonFoodCosts),
  };
}

// --- top selling items ----------------------------------------------------

function computeTopSelling(
  curRevenue: RevenueHistory[],
  prevRevenue: RevenueHistory[],
  side: 'food' | 'drinks'
): SellingItemRow[] {
  const cur: RevenueHistory[] = filterRevenueByOverallType(curRevenue, side);
  const prev: RevenueHistory[] = filterRevenueByOverallType(prevRevenue, side);

  // Aggregate current period by `${name}-${category}` (mirrors sellingItemsService).
  // The item name is kept in the value — re-deriving it from the key breaks on any
  // category name containing a hyphen (e.g. "Bar POP - Untable").
  const currentItems = new Map<string, { name: string; quantity: number; revenue: number }>();
  cur.forEach((rev) => {
    rev.revenueData.categories.forEach((category) => {
      category.items.forEach((item) => {
        if (item.price > 0) {
          const key = `${item.name}-${category.name}`;
          const agg = currentItems.get(key) || { name: item.name, quantity: 0, revenue: 0 };
          agg.quantity += item.quantity || 0;
          agg.revenue += getItemTotalRevenue(item, true);
          currentItems.set(key, agg);
        }
      });
    });
  });

  // Previous-period quantities by the same key, for the qty-change %.
  const previousQuantities = new Map<string, number>();
  prev.forEach((rev) => {
    rev.revenueData.categories.forEach((category) => {
      category.items.forEach((item) => {
        if (item.price > 0) {
          const key = `${item.name}-${category.name}`;
          previousQuantities.set(key, (previousQuantities.get(key) || 0) + (item.quantity || 0));
        }
      });
    });
  });

  return Array.from(currentItems.entries())
    .map(([key, data]) => {
      const prevQuantity = previousQuantities.get(key) || 0;
      const quantityChangePercent =
        prevQuantity > 0 ? ((data.quantity - prevQuantity) / prevQuantity) * 100 : data.quantity > 0 ? 100 : 0;
      return { name: data.name, quantity: data.quantity, quantityChangePercent, revenue: data.revenue };
    })
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, TOP_LIMIT);
}

// --- tracked dishes -------------------------------------------------------

function computeTrackedDishes(
  curRevenue: RevenueHistory[],
  prevRevenue: RevenueHistory[],
  settings: ReportSettings | undefined
): TrackedDishRow[] {
  const food = settings?.trackedDishes?.food ?? [];
  const drinks = settings?.trackedDishes?.drinks ?? [];
  if (food.length === 0 && drinks.length === 0) return [];

  interface DishAgg {
    quantityThisPeriod: number;
    quantityLastPeriod: number;
    revenueThisPeriod: number;
    revenueLastPeriod: number;
    category: 'food' | 'drinks';
  }
  const dishMap = new Map<string, DishAgg>();

  // Current period — only items configured as tracked dishes for their side.
  curRevenue.forEach((rev) => {
    const isFood = rev.revenueSourceType === RevenueSourceType.FOOD;
    const tracked = isFood ? food : drinks;
    rev.revenueData.categories.forEach((category) => {
      category.items.forEach((item) => {
        if (tracked.includes(item.name)) {
          const agg =
            dishMap.get(item.name) ||
            ({
              quantityThisPeriod: 0,
              quantityLastPeriod: 0,
              revenueThisPeriod: 0,
              revenueLastPeriod: 0,
              category: isFood ? 'food' : 'drinks',
            } as DishAgg);
          agg.quantityThisPeriod += item.quantity || 0;
          agg.revenueThisPeriod += getItemTotalRevenue(item, true);
          dishMap.set(item.name, agg);
        }
      });
    });
  });

  // Previous period — only for dishes already present (mirrors dishPerformanceService).
  prevRevenue.forEach((rev) => {
    const isFood = rev.revenueSourceType === RevenueSourceType.FOOD;
    const tracked = isFood ? food : drinks;
    rev.revenueData.categories.forEach((category) => {
      category.items.forEach((item) => {
        if (tracked.includes(item.name) && dishMap.has(item.name)) {
          const agg = dishMap.get(item.name)!;
          agg.quantityLastPeriod += item.quantity || 0;
          agg.revenueLastPeriod += getItemTotalRevenue(item, true);
        }
      });
    });
  });

  return Array.from(dishMap.entries())
    .map(([name, data]) => ({
      name,
      category: data.category,
      quantity: data.quantityThisPeriod,
      quantityChange:
        data.quantityLastPeriod > 0
          ? ((data.quantityThisPeriod - data.quantityLastPeriod) / data.quantityLastPeriod) * 100
          : data.quantityThisPeriod > 0
          ? 100
          : 0,
      revenue: data.revenueThisPeriod,
      revenueChange:
        data.revenueLastPeriod > 0
          ? ((data.revenueThisPeriod - data.revenueLastPeriod) / data.revenueLastPeriod) * 100
          : data.revenueThisPeriod > 0
          ? 100
          : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);
}

// --- highest spending products (PARITY EXCEPTION: incl-tax) ----------------

function computeHighestSpending(orders: OrderHistory[], side: 'food' | 'drinks'): SpendingProductRow[] {
  const sideOrders = filterOrdersByOverallType(orders, side);

  interface Agg {
    name: string;
    supplier: string;
    quantity: number;
    spent: number;
    category?: string;
    lines: Array<{ rawQuantity: number; quantityInfo?: QuantityInfo; articleId: string; platform: string; name?: string }>;
  }
  const spending = new Map<string, Agg>();

  sideOrders.forEach((order) => {
    order.productData.forEach((product) => {
      // Aggregation key mirrors highestSpendingProductsService:24 (`name_platform`).
      const key = `${product.name}_${order.platform}`;
      const agg =
        spending.get(key) ||
        ({ name: product.name, supplier: order.platform, quantity: 0, spent: 0, category: product.category, lines: [] } as Agg);
      const qty = getProductQuantity(product, order.platform);
      // Incl-tax spend to match the weekly email.
      agg.spent += (product.priceWithTaxes || product.price) * qty;
      // Quantity fallback mirrors highestSpendingProductsService:39 (Σ getProductQuantity).
      agg.quantity += qty;
      agg.lines.push({
        rawQuantity: product.rawQuantity,
        quantityInfo: product.quantityInfo,
        articleId: product.articleId,
        platform: order.platform,
        name: product.name,
      });
      spending.set(key, agg);
    });
  });

  return Array.from(spending.values())
    .sort((a, b) => b.spent - a.spent)
    .slice(0, TOP_LIMIT)
    .map((agg) => {
      const aggregated = aggregateQuantityInfos(agg.lines, `${agg.name}_${agg.supplier}`);
      const formatted = formatAggregatedQuantity(aggregated.totalRawQuantity, aggregated, agg.category);
      return {
        name: agg.name,
        supplier: agg.supplier,
        quantity: agg.quantity,
        spent: agg.spent,
        formattedQuantity: formatted?.displayText,
      };
    });
}

// --- price alerts ---------------------------------------------------------

function computePriceAlerts(priceAlerts: PriceAlert[], range: ReportRange): PriceAlertRow[] {
  // Keyed on `alert.toDate` (mirrors filterPriceAlertsByPeriod), filtered against
  // the unified report range so quarter/custom ranges also work. Most recent first.
  return priceAlerts
    .filter((alert) => {
      const toDate = new Date(alert.toDate);
      return !Number.isNaN(toDate.getTime()) && inRange(toDate, range);
    })
    .sort((a, b) => new Date(b.toDate).getTime() - new Date(a.toDate).getTime())
    .map((alert) => ({
      productName: alert.productName,
      platform: alert.platform,
      fromPrice: alert.fromPrice,
      toPrice: alert.toPrice,
      changePercent: alert.fromPrice !== 0 ? ((alert.toPrice - alert.fromPrice) / alert.fromPrice) * 100 : 0,
    }));
}

// --- entry point ----------------------------------------------------------

export interface OverviewReportInputs {
  orders: OrderHistory[];
  revenue: RevenueHistory[];
  priceAlerts: PriceAlert[];
  reportSettings: ReportSettings | undefined;
  range: ReportRange;
  previous: ReportRange | null;
}

// --- per-restaurant breakdown (all-restaurants / holding view only) --------

/** One restaurant's headline figures for the holding-company breakdown. */
export interface RestaurantBreakdownRow {
  restaurantId: string;
  name: string;
  /** Food + drinks revenue, ex-VAT (mirrors the snapshot total-revenue basis). */
  revenue: number;
  /** Food + drinks product costs (COGS), ex-VAT — the inslag numerator, so
   *  `costs / revenue === inslag` holds within the row. */
  costs: number;
  /** Inslag % = (food+drinks costs) / (food+drinks revenue), mirroring the snapshot. */
  inslag: number;
}

/**
 * Per-restaurant snapshot for the "all restaurants" holding view. Orders + revenue
 * carry a `restaurantId` in all-restaurants mode (added by the all-restaurants
 * endpoints), so we group the range-filtered entries by restaurant and compute the
 * same revenue / costs / inslag figures the combined snapshot uses. Sorted by
 * revenue desc. Restaurants with no activity in the period are omitted.
 */
export function computePerRestaurantOverview(
  orders: OrderHistory[],
  revenue: RevenueHistory[],
  range: ReportRange,
  restaurantNames: Record<string, string>
): RestaurantBreakdownRow[] {
  const curOrders = orders.filter((o) => inRange(new Date(o.orderDate), range));
  const curRevenue = revenue.filter((r) => inRange(new Date(r.date), range));

  const ordersByRestaurant = new Map<string, OrderHistory[]>();
  curOrders.forEach((o) => {
    const id = o.restaurantId || '';
    if (!id) return;
    (ordersByRestaurant.get(id) ?? ordersByRestaurant.set(id, []).get(id)!).push(o);
  });
  const revenueByRestaurant = new Map<string, RevenueHistory[]>();
  curRevenue.forEach((r) => {
    const id = r.restaurantId || '';
    if (!id) return;
    (revenueByRestaurant.get(id) ?? revenueByRestaurant.set(id, []).get(id)!).push(r);
  });

  const ids = new Set<string>([
    ...Array.from(ordersByRestaurant.keys()),
    ...Array.from(revenueByRestaurant.keys()),
  ]);
  return Array.from(ids)
    .map((id) => {
      const rOrders = ordersByRestaurant.get(id) ?? [];
      const rRevenue = revenueByRestaurant.get(id) ?? [];
      const foodRevenue = sideRevenue(rRevenue, 'food');
      const drinksRevenue = sideRevenue(rRevenue, 'drinks');
      const foodCosts = sideCosts(rOrders, 'food');
      const drinksCosts = sideCosts(rOrders, 'drinks');
      const revenueTotal = foodRevenue + drinksRevenue;
      const costsTotal = foodCosts + drinksCosts;
      return {
        restaurantId: id,
        name: restaurantNames[id] || id,
        revenue: revenueTotal,
        costs: costsTotal,
        inslag: inslag(costsTotal, revenueTotal),
      };
    })
    .sort((a, b) => b.revenue - a.revenue);
}

export function computeOverviewReport(inputs: OverviewReportInputs): OverviewReportData {
  const { orders, revenue, priceAlerts, reportSettings, range, previous } = inputs;

  const curOrders = orders.filter((o) => inRange(new Date(o.orderDate), range));
  const curRevenue = revenue.filter((r) => inRange(new Date(r.date), range));
  const prevOrders = previous ? orders.filter((o) => inRange(new Date(o.orderDate), previous)) : [];
  const prevRevenue = previous ? revenue.filter((r) => inRange(new Date(r.date), previous)) : [];

  return {
    snapshot: computeSnapshot(curOrders, curRevenue, prevOrders, prevRevenue),
    topSellingFood: computeTopSelling(curRevenue, prevRevenue, 'food'),
    topSellingDrinks: computeTopSelling(curRevenue, prevRevenue, 'drinks'),
    trackedDishes: computeTrackedDishes(curRevenue, prevRevenue, reportSettings),
    highestSpendingKitchen: computeHighestSpending(curOrders, 'food'),
    highestSpendingBar: computeHighestSpending(curOrders, 'drinks'),
    priceAlerts: computePriceAlerts(priceAlerts, range),
    comparisonHidden: range.comparisonHidden,
  };
}
